import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ChronologyEvent, ChronologyEventOverrides } from "~/api/model";
import { Badge } from "~/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { getOverriddenFields } from "../helpers/sortChronologyEvents";

interface ChronologyEditedBadgeProps {
  event: ChronologyEvent;
  /** Lets each call site match the badge height of its sibling badges. */
  className?: string;
}

/** Maps each overridable field to the i18n label shown in the "Edited" tooltip. */
const OVERRIDE_FIELD_LABEL_IDS = {
  eventDate: "chronology.field.date",
  datePrecision: "chronology.field.datePrecision",
  dateText: "chronology.field.dateText",
  eventType: "chronology.field.eventType",
  documentCategory: "chronology.field.documentCategory",
  provider: "chronology.field.provider",
  providerSpecialty: "chronology.field.providerSpecialty",
  facility: "chronology.field.facility",
  documentName: "chronology.field.documentName",
  documentType: "chronology.field.documentType",
  detail: "chronology.field.detail",
  sourcePage: "chronology.field.sourcePage",
  batesNumber: "chronology.field.batesNumber",
} satisfies Record<keyof ChronologyEventOverrides, string>;

/**
 * The "Edited" badge shown when an event carries manual overrides, with a
 * tooltip naming the overridden fields. Shared by the main chronology row and
 * the duplicates modal so reviewers can tell a corrected copy from a raw
 * extraction before permanently deleting a duplicate. Renders nothing when the
 * event is unedited.
 */
export const ChronologyEditedBadge: FC<ChronologyEditedBadgeProps> = ({
  event,
  className,
}) => {
  const intl = useIntl();

  if (!event.isEdited) return null;

  const overriddenFieldLabels = Array.from(getOverriddenFields(event)).map(
    (field) => intl.formatMessage({ id: OVERRIDE_FIELD_LABEL_IDS[field] })
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge variant="outline" className={cn("cursor-default", className)}>
            <FormattedMessage
              id="chronology.badge.edited"
              defaultMessage="Edited"
            />
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {overriddenFieldLabels.length > 0 ? (
          <FormattedMessage
            id="chronology.badge.editedFields"
            defaultMessage="Manually edited: {fields}"
            values={{ fields: overriddenFieldLabels.join(", ") }}
          />
        ) : (
          <FormattedMessage
            id="chronology.badge.editedGeneric"
            defaultMessage="Manually edited from the extracted values"
          />
        )}
      </TooltipContent>
    </Tooltip>
  );
};
